import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocationContext } from '../context/LocationContext';
import { usePermissions } from '../hooks/usePermissions';
import { NotificationProvider } from '../context/NotificationContext';
import { useMessageUnread } from '../context/MessageUnreadContext';
import { canShowQaChecksNav } from '../qa/tenantAccess';
import {
  MY_RECORDS_PATHS,
  buildMainNavigation,
  buildMyRecordsItems,
  buildProfileMenuItems,
  marketplaceNavItem,
  reportsNavItem,
} from './layout/navConfig';
import AppSidebar, { SidebarLinkItem } from './layout/AppSidebar';
import AppTopBar, { TopBarDestination } from './layout/AppTopBar';
import { myRecordsIcon, sidebarIcon } from './layout/sidebarIcons';
import LocationSelect from './LocationSelect';

export { useLocationContext } from '../context/LocationContext';

interface LayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'staffly:sidebarCollapsed';

function isPathActive(pathname: string, href: string, search = '') {
  if (href === '/') return pathname === '/';
  const queryIndex = href.indexOf('?');
  if (queryIndex >= 0) {
    const path = href.slice(0, queryIndex);
    if (pathname !== path) return false;
    const expected = new URLSearchParams(href.slice(queryIndex + 1));
    const actual = new URLSearchParams(search);
    let matches = true;
    expected.forEach((value, key) => {
      if (actual.get(key) !== value) matches = false;
    });
    return matches;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

function isMessagesActive(pathname: string) {
  return pathname === '/messages' || pathname === '/my-messages';
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { employee, logout } = useAuth();
  const {
    selectedLocationId,
    setSelectedLocationId,
    locations,
    locationsLoading,
    isSelectedLocationInactive,
    employeeDetailsReportLocationLocked,
    documentsPoliciesLocationLocked,
    lockDocumentsPoliciesLocation,
    unlockDocumentsPoliciesLocation,
  } = useLocationContext();
  const {
    hasDashboard,
    hasManagerInbox,
    hasTaskManagement,
    hasMessageManagement,
    hasHolidayManagement,
    hasIncidentManagement,
    hasEmployeeManagement,
    hasScheduling,
    hasAttendanceAdmin,
    hasTimecards,
    hasTimeStationClockInOut,
    hasReports,
    hasSettings,
    hasJobMonitor,
    hasLeavePolicies,
    hasJobApplicantTracking,
    loading: permissionsLoading,
    hasPermission,
    hasSuperAdminRole,
    securityCommandControlLogEnabled,
    hasDocumentsPolicies,
  } = usePermissions();
  const { unreadCount: messageUnreadCount } = useMessageUnread();
  const canShowMessages = hasPermission('my_messages') || hasMessageManagement;
  const showFeatureAccessLink = hasSettings;
  const showSecurityCommandControlLogLink =
    !permissionsLoading &&
    securityCommandControlLogEnabled &&
    hasPermission('security_command_control_log');

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const pathname = location.pathname;
  const isDocumentsPoliciesRoute = pathname.startsWith('/documents-policies');
  const locationSelectLocked =
    employeeDetailsReportLocationLocked || documentsPoliciesLocationLocked;
  const inboxDelegations =
    pathname === '/my-team' && new URLSearchParams(location.search).get('scope') === 'all';
  const isActive = (path: string) => {
    if (inboxDelegations && path === '/my-team') return false;
    if (inboxDelegations && path === '/inbox') return true;
    return isPathActive(pathname, path, location.search);
  };

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore quota */
    }
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isDocumentsPoliciesRoute) return;
    lockDocumentsPoliciesLocation();
    return () => {
      unlockDocumentsPoliciesLocation();
    };
  }, [isDocumentsPoliciesRoute, lockDocumentsPoliciesLocation, unlockDocumentsPoliciesLocation]);

  const myRecordsActive = MY_RECORDS_PATHS.some(
    (p) => !(inboxDelegations && p === '/my-team') && isPathActive(pathname, p),
  );

  const mainNavigation = useMemo(
    () => buildMainNavigation({
      hasDashboard,
      hasManagerInbox,
      hasEmployeeManagement,
      hasScheduling,
      hasAttendanceAdmin,
      hasTimecards,
      hasTimeStationClockInOut,
    }),
    [hasDashboard, hasManagerInbox, hasEmployeeManagement, hasScheduling, hasAttendanceAdmin, hasTimecards, hasTimeStationClockInOut],
  );

  const myRecordsItems = useMemo(
    () => buildMyRecordsItems({
      canShowMessages,
      hasMyLicenses: hasPermission('my_licenses'),
    }),
    [canShowMessages, hasPermission],
  );

  const showMarketplaceNav =
    hasPermission('marketplace_proposals') || hasScheduling;

  const profileMenuItems = useMemo(
    () => buildProfileMenuItems({
      hasSettings,
      hasLeavePolicies,
      hasHolidayManagement,
      hasScheduling,
      hasIncidentManagement,
      showSecurityCommandControlLogLink,
      showFeatureAccessLink,
      hasTaskManagement,
      hasMessageManagement,
      hasJobMonitor,
      hasJobApplicantTracking,
      hasDocumentsPolicies,
      hasQaChecks: canShowQaChecksNav(hasPermission('qa_checks')),
    }),
    [
      hasSettings,
      hasLeavePolicies,
      hasHolidayManagement,
      hasScheduling,
      hasIncidentManagement,
      showSecurityCommandControlLogLink,
      showFeatureAccessLink,
      hasTaskManagement,
      hasMessageManagement,
      hasJobMonitor,
      hasJobApplicantTracking,
      hasDocumentsPolicies,
      hasReports,
      hasPermission,
    ],
  );

  const timeStationActive = isActive('/settings?section=time-stations');
  const settingsActive = pathname.startsWith('/settings') && !timeStationActive;

  const links: SidebarLinkItem[] = mainNavigation.map((item) => ({
    label: item.label,
    href: item.href,
    icon: sidebarIcon(item.href),
    active: isActive(item.href),
  }));

  const trailingLinks: SidebarLinkItem[] = [];
  if (showMarketplaceNav) {
    trailingLinks.push({
      label: marketplaceNavItem.label,
      href: marketplaceNavItem.href,
      icon: sidebarIcon(marketplaceNavItem.href),
      active: pathname.startsWith('/shift-marketplace'),
    });
  }
  if (hasReports) {
    trailingLinks.push({
      label: reportsNavItem.label,
      href: reportsNavItem.href,
      icon: sidebarIcon(reportsNavItem.href),
      active: pathname.startsWith('/reports'),
    });
  }

  const myRecords = {
    label: 'My Records',
    icon: myRecordsIcon,
    active: myRecordsActive,
    badge: messageUnreadCount,
    items: myRecordsItems.map((item) => ({
      label: item.label,
      href: item.href,
      icon: sidebarIcon(item.href),
      active: item.href === '/messages' ? isMessagesActive(pathname) : isActive(item.href),
      badge: item.href === '/messages' ? messageUnreadCount : undefined,
    })),
  };

  const settingsLink: SidebarLinkItem | null = hasSettings
    ? {
        label: 'Settings',
        href: '/settings',
        icon: sidebarIcon('/settings'),
        active: settingsActive,
      }
    : null;

  const destinations: TopBarDestination[] = [
    ...links.map((item) => ({ label: item.label, href: item.href })),
    ...myRecords.items.map((item) => ({ label: item.label, href: item.href })),
    ...trailingLinks.map((item) => ({ label: item.label, href: item.href })),
    ...profileMenuItems.map((item) => ({ label: item.label, href: item.href })),
  ];

  const roleLabel = employee?.position?.title
    || (hasSuperAdminRole ? 'Administrator' : 'Employee');

  const locationTitle = employeeDetailsReportLocationLocked
    ? 'Location is fixed to All locations while Employee details report is open.'
    : documentsPoliciesLocationLocked
      ? 'Location is fixed to All locations while Documents & Policies is open.'
      : isSelectedLocationInactive
        ? 'This location is inactive — view only.'
        : undefined;

  return (
    <NotificationProvider>
      <div className="flex h-screen overflow-hidden bg-[#F4F7FB]">
        {mobileOpen && (
          <button
            type="button"
            className="fixed inset-0 z-30 bg-slate-900/40 md:hidden"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
        )}
        <AppSidebar
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          onToggleCollapse={() => setCollapsed((value) => !value)}
          onNavigate={() => setMobileOpen(false)}
          links={links}
          trailingLinks={trailingLinks}
          myRecords={myRecords}
          settingsLink={settingsLink}
          footer={
            <div className="border-t border-white/10 p-3 md:hidden">
              <LocationSelect
                locations={locations}
                value={locationSelectLocked ? null : selectedLocationId}
                onChange={setSelectedLocationId}
                disabled={locationSelectLocked}
                loading={locationsLoading}
                title={locationTitle}
                fullWidth
              />
            </div>
          }
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <AppTopBar
            onOpenMobileMenu={() => setMobileOpen(true)}
            employee={employee}
            roleLabel={roleLabel}
            profileItems={profileMenuItems}
            onSignOut={async () => {
              try {
                await logout();
              } finally {
                navigate('/login', { replace: true });
              }
            }}
            canShowMessages={canShowMessages}
            destinations={destinations}
            locations={locations}
            selectedLocationId={selectedLocationId}
            onLocationChange={setSelectedLocationId}
            locationDisabled={locationSelectLocked}
            locationsLoading={locationsLoading}
            locationTitle={locationTitle}
            locationClassName="hidden md:flex"
          />
          <main className={`min-h-0 flex-1 ${isMessagesActive(pathname) ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            {children}
          </main>
        </div>
      </div>
    </NotificationProvider>
  );
};

export default Layout;
